import React, { useState, useEffect, useCallback } from 'react';

import { Flex, RadioGroup, useTheme } from '@chakra-ui/react';
import { Table, TableBody, TableCell, TableRow, Select, MenuItem, Grid } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import GoalProgressBar from 'components/GoalProgressBar';
import { useGoalsWeight } from 'components/GoalsWeight/GoalsWeightContext';
import RichTextEditor from 'components/RichTextEditor';
import SkeletonLoader from 'components/SkeletonLoader';
import TableHeader from 'components/TableComponents/TableHeader';
import TextParseComponent from 'components/feed/TextParseComponent';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import ReviewQuestion from 'models/reviewQuestion';
import Task1 from 'models/task1';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useReviewTemplate } from 'screens/admin/Reviews/ReviewTemplate/common/ReviewTemplateContext';
import styled, { css } from 'styled-components';
import { StyledBox, StyledGrid, StyledTooltip, StyledTypography, VerticalFlexContainer } from 'styles/common';

import { REVIEW_TEXTS, REVIEW_ANSWER_TYPE_MAP, REVIEW_QUESTION_TYPES } from 'constants/ProjectConstants';
import { getMultiSelectOptionsText, getTaskDetailsRoute } from 'constants/helper';
import theme from 'constants/theme';

import { getGoalForm, updateQuestionResponse } from 'services/performanceReview';
import { showErrorSnackbar, showInfoSnackbar } from 'services/snackbar';
import { getReviewTemplateQuestions } from 'services/templates/review';

import NumberRating from 'uiToolkit/NumberRating';
import StarRating from 'uiToolkit/StarRating';
import { Typography18 } from 'uiToolkit/Typography';
import { Radio } from 'uiToolkitV1/Radio';

import { useReview } from '../ReviewContext';
import ControlledTextField from './ControlledTextField';
import DisplayInfo from './DisplayInfo';
import PeerFeedbackComp from './PeerFeedbackComp';

const Container = styled.div`
  width: 100%;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const StyledTable = styled(Table)`
  border: 1px solid ${(props) => props.theme.palette.borders.lightGrey};
  background-color: ${(props) => props.theme.palette.background.paper};

  .MuiTablePagination-selectRoot,
  .MuiTablePagination-input {
    display: none;
  }

  .MuiAccordion-rounded {
    border-radius: 0px;
  }
`;

const StyledTableCell = styled(TableCell)`
  border-color: #e1e1e1 !important;
  ${(props) =>
    props.border &&
    css`
      border: ${props.border};
    `};
  ${(props) =>
    props.width &&
    css`
      width: ${props.width};
      max-width: ${props.width};
    `};
  vertical-align: top;
`;

const StyledTableRow = styled(TableRow)`
  ${(props) =>
    props.isSelected &&
    css`
      background-color: ${props.theme.palette.background.lightBlue};
    `};

  ${(props) =>
    props.fullwidth &&
    css`
      width: 100%;
    `};
`;

const StickyGridContainer = styled(Grid)`
  min-height: 40vh;
  max-height: 100vh;
  position: sticky;
  top: 9vh;
  overflow-y: auto;
`;

const StyledSelect = styled(Select)`
  .Mui-disabled {
    color: ${(props) => props.theme.palette.text.secondary};
  }
  ${({ showError }) =>
    showError &&
    `
      fieldset{
        border: 1px solid #EF5D7E;
      }
    `};
`;

const RatingContainer = styled(StyledGrid)`
  background: ${(props) => props.theme.palette.background.lightGrey};
`;

export const getTemplatePreviewQuestion = (previewTemplateView, allVariantsQuestions) => {
  const { PEER, MANAGER, UPWARD, SELF, DLM } = allVariantsQuestions;
  switch (previewTemplateView) {
    case 'SELF':
      return SELF;
    case 'MANAGER':
      return MANAGER;
    case 'UPWARD':
      return UPWARD;
    case 'PEER':
      return PEER;
    case 'DLM':
      return DLM;
    default:
      return null;
  }
};

export const MultiSelect = ({
  answerOptions,
  selectedAnswerOptions,
  submitAnswer = () => {},
  width,
  isSubmitted,
  isRequired,
  isSubmitClicked,
  minOptions,
  maxOptions,
  disabled,
}) => {
  const selectedAnswerIds = selectedAnswerOptions ? selectedAnswerOptions.map((item) => item.id) : '';
  const selectedAnswerOptionsReferential = answerOptions.filter((item) => selectedAnswerIds.indexOf(item.id) > -1);
  const dispatch = useDispatch();
  return (
    <VerticalFlexContainer>
      <StyledTooltip title={selectedAnswerOptionsReferential?.map((item) => item.description).join(', ') || ''}>
        <StyledSelect
          variant="outlined"
          fullWidth
          displayEmpty
          multiple
          value={selectedAnswerOptionsReferential}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
            style: { maxWidth: width },
          }}
          SelectDisplayProps={{
            style: {
              maxHeight: '3.5rem',
              overflowY: 'auto',
              padding: '0.75rem 1.5rem 0.75rem 0.75rem',
              backgroundColor: theme.palette.background.paper,
            },
          }}
          renderValue={(selected) => {
            let text = '';
            if (selected.length === 0) {
              text = `Select ${isRequired ? '*' : ''}`;
            } else {
              text = selected.map((item) => item.description).join(', ');
            }
            return (
              <StyledTypography style={{ whiteSpace: 'normal' }} variant="body2">
                {text}
              </StyledTypography>
            );
          }}
          onChange={({ target }) => {
            if (target.value.length > maxOptions) {
              dispatch(showInfoSnackbar('Maximum allowed options already selected'));
              return;
            }
            submitAnswer('rating', target.value);
          }}
          disabled={isSubmitted || disabled}
          showError={
            (!selectedAnswerOptions || selectedAnswerOptions.length < minOptions) && isRequired && isSubmitClicked
          }
        >
          {answerOptions.map((option, optionIndex) => {
            const isSelected = selectedAnswerOptions.map((item) => item.id).indexOf(option.id) > -1;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <MenuItem key={`menu-option-${optionIndex}`} value={option}>
                <Checkbox color="primary" checked={isSelected} />
                <StyledTypography
                  style={{ whiteSpace: 'normal' }}
                  variant="body2"
                  color={isSelected ? '#3a8ee9' : 'textPrimary'}
                >
                  {option.description}
                </StyledTypography>
              </MenuItem>
            );
          })}
        </StyledSelect>
      </StyledTooltip>
      <StyledTypography topGutter={1} variant="caption" color="textSecondary">
        {getMultiSelectOptionsText(minOptions, maxOptions, answerOptions.length)}
        {isRequired ? ' *' : ''}
      </StyledTypography>
    </VerticalFlexContainer>
  );
};

export const DropDownSelect = ({
  answerOptions,
  selectedAnswerOptions,
  submitAnswer = () => {},
  width,
  isSubmitted,
  isRequired,
  isSubmitClicked,
  isGoalSectionEditable,
  onSelectQuestion = () => {},
}) => {
  const value = answerOptions.filter(
    (answerItem) => selectedAnswerOptions?.map((item) => item.id).indexOf(answerItem.id) > -1
  );

  return (
    <StyledTooltip title={value[0]?.description || ''} placement="top">
      <StyledSelect
        variant="outlined"
        fullWidth
        displayEmpty
        value={value[0] || ''}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
          style: { maxWidth: width },
        }}
        SelectDisplayProps={{
          style: {
            maxHeight: '3.5rem',
            overflowY: 'auto',
            padding: '0.75rem 1.5rem 0.75rem 0.75rem',
            backgroundColor: theme.palette.background.paper,
          },
        }}
        renderValue={(val) => (
          <StyledTypography style={{ whiteSpace: 'normal' }} variant="body2">
            {val ? val.description : `Select ${isRequired ? '*' : ''}`}
          </StyledTypography>
        )}
        onChange={({ target }) => {
          submitAnswer('rating', target.value);
        }}
        disabled={isSubmitted || !isGoalSectionEditable}
        showError={(!selectedAnswerOptions || !selectedAnswerOptions[0]) && isRequired && isSubmitClicked}
        onOpen={onSelectQuestion}
      >
        {answerOptions.map((option, optionIndex) => (
          <MenuItem
            // eslint-disable-next-line react/no-array-index-key
            key={`menu-option-${optionIndex}`}
            value={option}
            style={{
              borderBottom: `1px solid ${
                optionIndex !== answerOptions.length - 1 ? theme.palette.borders.lightGrey : 'transparent'
              }`,
            }}
          >
            <StyledTypography style={{ whiteSpace: 'normal' }} variant="body2">
              {option.description}
            </StyledTypography>
          </MenuItem>
        ))}
      </StyledSelect>
    </StyledTooltip>
  );
};

const commonColumnsList = (view, isManagerView) => [
  {
    id: 'rating',
    title: 'Assessment',
    width: view === 'goalsView' ? '20%' : '30%',
    component: ({
      answerOptions,
      selectedAnswerOptions,
      submitAnswer,
      loading,
      answerType,
      width,
      isSubmitted,
      isRequired,
      minOptions,
      maxOptions,
      isSubmitClicked,
      isGoalSectionEditable,
      onSelectQuestion,
      isViewOnlyModeEnabled,
    }) => {
      if (loading) {
        return <SkeletonLoader skeletons={['80%']} />;
      }
      const value = answerOptions.filter(
        (answerItem) => selectedAnswerOptions?.map((item) => item.id).indexOf(answerItem.id) > -1
      );

      const getAnswerComponent = () => {
        if (isViewOnlyModeEnabled) {
          return <span>-</span>;
        }
        switch (answerType) {
          case REVIEW_ANSWER_TYPE_MAP.MULTI_SELECT:
            return !isGoalSectionEditable ? (
              <div>
                <Typography18 color="textprimary">
                  {selectedAnswerOptions?.length
                    ? selectedAnswerOptions.map((ans) => ans.description).join(', ')
                    : 'NA'}
                </Typography18>
              </div>
            ) : (
              <MultiSelect
                answerOptions={answerOptions}
                selectedAnswerOptions={selectedAnswerOptions}
                submitAnswer={submitAnswer}
                loading={loading}
                answerType={answerType}
                width={width}
                isSubmitted={isSubmitted}
                isRequired={isRequired}
                isSubmitClicked={isSubmitClicked}
                minOptions={minOptions}
                maxOptions={maxOptions}
                disabled={!isGoalSectionEditable}
              />
            );
          case REVIEW_ANSWER_TYPE_MAP.DROP_DOWN:
            return (
              answerOptions?.length > 0 &&
              (isSubmitted || !isGoalSectionEditable ? (
                <div>
                  <Typography18 color="textprimary">
                    {selectedAnswerOptions.length
                      ? selectedAnswerOptions.map((ans) => ans.description).join(', ')
                      : 'NA'}
                  </Typography18>
                </div>
              ) : (
                <DropDownSelect
                  answerOptions={answerOptions}
                  selectedAnswerOptions={selectedAnswerOptions}
                  submitAnswer={submitAnswer}
                  width={width}
                  isSubmitted={isSubmitted}
                  isRequired={isRequired}
                  isSubmitClicked={isSubmitClicked}
                  isGoalSectionEditable={isGoalSectionEditable}
                  onSelectQuestion={onSelectQuestion}
                />
              ))
            );

          case REVIEW_ANSWER_TYPE_MAP.STAR_RATING:
            return (
              <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start" marginLeft="-16px">
                <StarRating
                  onChange={(newValue) => {
                    submitAnswer(
                      'rating',
                      answerOptions.filter((ans) => ans.displayOrder === newValue)
                    );
                  }}
                  max={answerOptions.length}
                  prevVal={value[0]?.displayOrder || 0}
                  size={isManagerView ? 'smallest' : 'small'}
                  readOnly={isSubmitted || !isGoalSectionEditable}
                />
                {!(isSubmitted || !isGoalSectionEditable) && (
                  <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start">
                    {isRequired && (
                      <StyledTypography
                        color={isSubmitClicked && !selectedAnswerOptions.length ? 'red' : 'textPrimary'}
                        weight={isSubmitClicked && !selectedAnswerOptions.length ? 'bold' : 'normal'}
                      >
                        *
                      </StyledTypography>
                    )}
                  </StyledBox>
                )}
              </StyledBox>
            );
          case REVIEW_ANSWER_TYPE_MAP.NUMBER_RATING:
            return (
              <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start">
                <NumberRating
                  data={answerOptions}
                  onSelect={(selectedId) => {
                    submitAnswer(
                      'rating',
                      answerOptions.filter((ans) => ans.id === selectedId)
                    );
                  }}
                  selectedId={value[0]?.id || null}
                  descriptionKey="score"
                  error={isRequired && isSubmitClicked && !selectedAnswerOptions.length}
                  readOnly={isSubmitted || !isGoalSectionEditable}
                />
                {!(isSubmitted || !isGoalSectionEditable) && (
                  <StyledBox flexcontainer justifycontent="flex-start" alignitems="flex-start">
                    {isRequired ? '*' : ''}
                  </StyledBox>
                )}
              </StyledBox>
            );
          default:
            return <span>NA</span>;
        }
      };

      return <>{getAnswerComponent()}</>;
    },
  },
  {
    id: 'feedback',
    title: 'Comments/Examples',
    width: view === 'goalsView' ? '45%' : '40%',
    component: ({
      feedbackText,
      feedbackTextHtml,
      submitAnswer,
      loading,
      isSubmitted,
      enableFeedbackText,
      isRequired,
      isTextRequired,
      isSubmitClicked,
      isViewOnlyModeEnabled,
    }) => {
      let returnValue;
      if (enableFeedbackText) {
        if (isSubmitted) {
          returnValue = (
            <div>
              <RichTextEditor
                placeholder="NA"
                initialText={feedbackTextHtml}
                plainText={feedbackText}
                showToolbar={false}
                readOnly
              />
            </div>
          );
        } else {
          returnValue = isViewOnlyModeEnabled ? (
            <span>-</span>
          ) : (
            <ControlledTextField
              defaultText={feedbackTextHtml || ''}
              plainDefaulText={feedbackText || ''}
              placeholder={`Please provide a basis for your assessment. If possible, with an example. ${
                isRequired && isTextRequired ? '*' : ''
              }`}
              onSubmit={(htmlText, plainText) => submitAnswer('feedbackText', { htmlText, plainText })}
              loading={loading}
              disabled={isSubmitted}
              isRequired={isRequired && isTextRequired && isSubmitClicked}
            />
          );
        }
      } else {
        returnValue = isViewOnlyModeEnabled ? <span>-</span> : <span>NA</span>;
      }
      return returnValue;
    },
  },
];

const columnsList = (isManagerView, updatedFirstColumnName) => ({
  goalsView: [
    {
      id: 'name',
      title: updatedFirstColumnName,
      width: '20%',
      component: ({ goal, goToTaskDetails, openWeightagePanel, employeeId, isShowGoalWeightage }) => (
        <StyledBox>
          <StyledTypography
            wordBreak
            variant="body2"
            color="textPrimary"
            onClick={(e) => {
              if (goToTaskDetails) goToTaskDetails(e, { taskId: goal.id, type: goal.type });
            }}
          >
            <TextParseComponent text={goal.text} res={goal.macros} />
          </StyledTypography>
          {isShowGoalWeightage && (
            <StyledTypography
              variant="caption"
              color="textSecondary"
              onClick={() => {
                if (openWeightagePanel) openWeightagePanel();
              }}
            >
              Weightage: <b>{goal.toIds.filter((emp) => emp.id === employeeId)[0]?.weightage}%</b>
            </StyledTypography>
          )}
        </StyledBox>
      ),
    },
    {
      id: 'goalProgress',
      title: 'Progress',
      width: '15%',
      component: ({ goal }) => (
        <>
          <GoalProgressBar goal={goal} />
          <StyledTypography variant="body2" display="inline">
            {`${Number(goal.getGoalProgress(0) || 0)}%`}
          </StyledTypography>
        </>
      ),
    },
    ...commonColumnsList('goalsView', isManagerView),
  ],
  questionsView: [
    {
      id: 'name',
      title: 'Question',
      width: '30%',
      component: ({ questionText, allVariantsQuestions, hasCustomDescription, previewTemplateView }) => {
        if (hasCustomDescription && allVariantsQuestions) {
          return (
            <StyledTypography wordBreak variant="body2" color="textPrimary">
              {getTemplatePreviewQuestion(previewTemplateView, allVariantsQuestions)}
            </StyledTypography>
          );
        }
        return (
          <StyledTypography wordBreak variant="body2" color="textPrimary">
            {questionText}
          </StyledTypography>
        );
      },
    },
    ...commonColumnsList('questionsView', isManagerView),
  ],
});

const GoalQuestions = ({
  goal,
  questions,
  submitAnswer,
  columnsView,
  isSubmitClicked,
  isManagerView,
  isShowGoalWeightage,
  refreshData,
  isGoalSectionEditable,
  isPreview,
  previewTemplateView,
}) => {
  const {
    state: { peerFeedback, selectedReviewInfo },
    showPeerFeedback,
  } = useReview();

  const { showSidePanel, hideSidePanel } = useGoalsWeight();

  const history = useHistory();
  const { employeeId } = useParams();

  const goToTaskDetails = (e, { taskId, type }) => {
    e.stopPropagation();
    const route = getTaskDetailsRoute({
      location: history.location,
      ids: [taskId],
      employee: { id: employeeId },
      type,
    });
    history.push(route);
  };

  const onSuccess = () => {
    refreshData();
    hideSidePanel();
  };

  const onCancel = () => {
    hideSidePanel();
  };

  const openWeightagePanel = () => {
    showSidePanel({
      onSuccess,
      onCancel,
      employeeId,
      canEdit: true,
    });
  };

  const onSelectQuestion = () => {
    if (
      peerFeedback.questionId &&
      peerFeedback.questionId === questions[0].id &&
      (!goal || peerFeedback.goalId === goal.id)
    )
      return;
    showPeerFeedback({
      goalId: goal ? goal.id : null,
      questionId: questions[0].id,
      answerType: questions[0].answerType,
      answerOptions: questions[0].answerOptions,
    });
  };

  return (
    <StyledTableRow
      fullwidth
      key={questions[0].id}
      onClick={onSelectQuestion}
      isSelected={
        isManagerView && peerFeedback.questionId === questions[0].id && (!goal || peerFeedback.goalId === goal.id)
      }
    >
      {questions.slice(0, 1).map(
        (
          {
            id,
            answerOptions,
            selectedAnswerOptions,
            feedbackText,
            feedbackTextHtml,
            loading,
            answerType,
            enableFeedbackText,
            description,
            isRequired,
            isTextRequired,
            minOptions,
            maxOptions,
            // eslint-disable-next-line no-shadow
            isViewOnlyModeEnabled,
            hasCustomDescription,
            providerToDescription,
          },
          qIndex
        ) => (
          <>
            {columnsList(isManagerView)[columnsView].map(({ width, component }, colIndex) => (
              <StyledTableCell
                // eslint-disable-next-line react/no-array-index-key
                key={`cell-${id}-${colIndex}`}
                border={qIndex === questions.length - 1 ? 'none' : ''}
              >
                {component({
                  goalName: goal ? goal.text : null,
                  goal,
                  answerOptions,
                  selectedAnswerOptions,
                  feedbackText,
                  feedbackTextHtml,
                  loading,
                  answerType,
                  enableFeedbackText,
                  isRequired,
                  isTextRequired,
                  minOptions,
                  maxOptions,
                  questionText: description,
                  submitAnswer: (field, answer) => {
                    submitAnswer({
                      questionId: id,
                      goalId: goal ? goal.id : null,
                      field,
                      answer,
                    });
                  },
                  width,
                  isSubmitClicked,
                  isSubmitted: isPreview
                    ? false
                    : selectedReviewInfo.isSubmitted === null || selectedReviewInfo.isSubmitted,
                  goToTaskDetails,
                  openWeightagePanel,
                  employeeId,
                  isShowGoalWeightage,
                  isGoalSectionEditable,
                  onSelectQuestion,
                  isViewOnlyModeEnabled,
                  hasCustomDescription,
                  allVariantsQuestions: providerToDescription,
                  previewTemplateView,
                })}
              </StyledTableCell>
            ))}
          </>
        )
      )}
    </StyledTableRow>
  );
};

const GoalForm = ({
  reviewId,
  employeeId,
  providerId,
  isManagerView,
  isSubmitClicked,
  isShowGoalRating,
  isShowGoalWeightage,
  isGoalSectionEditable,
  responseId,
  refetchForm,
  isPreview,
}) => {
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState(null);
  const [columnsView, setColumnsView] = useState('goalsView');
  const [goalRating, setGoalRating] = useState(null);
  const { showPeerFeedback } = useReview();
  const [previewTemplateView, setPreviewTemplateView] = useState('SELF');
  const { colors } = useTheme();

  const { keyResultsName } = useOrganisationSettings();

  const { templateId } = useParams();

  const dispatch = useDispatch();

  // Overriding things as in normal review form(not template preview), useReviewTempalte will be undefined and will break UI.
  const { state: { META = {} } = {} } = useReviewTemplate();

  const fetchData = () => {
    setLoading(true);
    if (isPreview) {
      if (templateId) {
        getReviewTemplateQuestions({
          templateId,
          section: REVIEW_QUESTION_TYPES.PERFORMANCE,
        })
          .then(({ goalQuestions }) => {
            setGoals(
              goalQuestions.map((tempQuestion) => {
                const goal = new Task1({ text: 'Dummy Goal' });
                return {
                  goal,
                  questions: [{ ...ReviewQuestion.fromJSON(tempQuestion.question), loading: false }],
                };
              })
            );
            setColumnsView(META?.templateSetting?.isEnableGoalLevelQuestion ? 'goalsView' : 'questionsView');
          })
          .catch((err) => console.error(err))
          .finally(() => setLoading(false));
      }
    } else {
      getGoalForm({
        reviewId,
        employeeId,
        providerId,
        responseId,
      })
        .then((res) => {
          setGoalRating({
            averageGoalRating: res.averageGoalRating,
            maxGoalRating: res.maxGoalRating,
          });
          setGoals(
            res.goalQuestions.map(({ goal, questions }) => ({
              goal,
              questions: questions.map((question) => ({ ...question, loading: false })),
            }))
          );
          setColumnsView(res.goalQuestions.filter((r) => r.goal).length === 0 ? 'questionsView' : 'goalsView');
          try {
            showPeerFeedback({
              goalId: res.goalQuestions[0].goal.id,
              questionId: res.goalQuestions[0].questions[0].id,
              answerType: res.goalQuestions[0].questions[0].answerType,
              answerOptions: res.goalQuestions[0].questions[0].answerOptions,
            });
          } catch (e) {
            console.error(e);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const answerQuestion = useCallback(({ goalId, questionId, field, answer }) => {
    // send dummy API call here
    // add loader here.

    let currentFeedBackText = '';
    let currentFeedBackTextHtml = '';
    let currentSelectedAnswerOptions = [];

    setGoals((prev) => {
      if (Array.isArray(prev))
        return prev.map((g) => {
          const { goal, questions } = g;

          if (goal && goal.id !== String(goalId)) return g;

          const mappedQuestions = questions.map((question) => {
            if (question.id !== questionId) return question;

            currentFeedBackText = question.feedbackText;
            currentFeedBackTextHtml = question.feedbackTextHtml;
            currentSelectedAnswerOptions = question.selectedAnswerOptions;

            return {
              ...question,
              loading: true,
            };
          });

          return {
            goal,
            questions: mappedQuestions,
          };
        });

      return null;
    });

    if (isPreview) {
      let selectedAnswerOptions;
      if (field === 'rating') {
        selectedAnswerOptions = Array.isArray(answer) ? answer : [answer];
      } else {
        selectedAnswerOptions = currentSelectedAnswerOptions;
      }
      const feedbackText = field === 'feedbackText' ? answer.plainText : currentFeedBackText;
      const feedbackTextHtml = field === 'feedbackText' ? answer.htmlText : currentFeedBackTextHtml;
      setGoals((prev) => {
        if (Array.isArray(prev))
          return prev.map((g) => {
            const { goal, questions } = g;

            const mappedQuestions = questions.map((question) => {
              if (question.id !== questionId) return question;

              if (field === 'rating' || field === 'feedbackText') {
                return {
                  ...question,
                  loading: false,
                  selectedAnswerOptions: Array.isArray(selectedAnswerOptions)
                    ? selectedAnswerOptions
                    : [selectedAnswerOptions],
                  feedbackText,
                  feedbackTextHtml,
                };
              }

              return question;
            });

            return {
              goal,
              questions: mappedQuestions,
            };
          });
        return null;
      });
    } else {
      let selectedAnswerOptions;
      if (field === 'rating') {
        selectedAnswerOptions = Array.isArray(answer) ? answer : [answer];
      } else {
        selectedAnswerOptions = currentSelectedAnswerOptions;
      }
      updateQuestionResponse({
        reviewId,
        goalId,
        employeeId,
        questionId,
        feedbackText: field === 'feedbackText' ? answer.plainText : currentFeedBackText,
        feedbackTextHtml: field === 'feedbackText' ? answer.htmlText : currentFeedBackTextHtml,
        selectedAnswerOptions,
        sourceForm: 'Goal form',
      })
        .then((entity) => {
          const {
            id: quesId,
            // eslint-disable-next-line no-shadow
            selectedAnswerOptions,
            feedbackText,
            feedbackTextHtml,
          } = entity?.question ?? {};
          setGoalRating({
            averageGoalRating: entity.averageRating,
            maxGoalRating: entity.maxRating,
          });
          setGoals((prev) => {
            if (Array.isArray(prev))
              return prev.map((g) => {
                const { goal, questions } = g;

                if (goal && goal.id !== String(goalId)) return g;

                const mappedQuestions = questions.map((question) => {
                  if (question.id !== quesId) return question;

                  if (field === 'rating' || field === 'feedbackText') {
                    return {
                      ...question,
                      loading: false,
                      selectedAnswerOptions: Array.isArray(selectedAnswerOptions)
                        ? selectedAnswerOptions
                        : [selectedAnswerOptions],
                      feedbackText,
                      feedbackTextHtml,
                    };
                  }

                  return question;
                });

                return {
                  goal,
                  questions: mappedQuestions,
                };
              });
            return null;
          });
          dispatch(showInfoSnackbar('Form autosaved.'));
        })
        .catch((err) => {
          setGoals((prev) => {
            if (Array.isArray(prev))
              return prev.map((g) => {
                const { goal, questions } = g;

                if (goal && goal.id !== String(goalId)) return g;

                const mappedQuestions = questions.map((question) => {
                  if (question.id !== questionId) return question;

                  return {
                    ...question,
                    loading: false,
                  };
                });

                return {
                  goal,
                  questions: mappedQuestions,
                };
              });
            return null;
          });
          dispatch(showErrorSnackbar(err.message || 'Some Error Occured while fetching Goal Form'));
        });
    }
  }, []);

  const showLoader = () => (
    <DisplayInfo>
      <SkeletonLoader skeletons={['100%', '100%']} />
    </DisplayInfo>
  );

  const showNoGoalsMessage = () => (
    <DisplayInfo>
      <StyledTypography variant="h6" color="textSecondaryLight" align="center" topGutter={4} bottomGutter={4}>
        {REVIEW_TEXTS.GOALS.NO_DATA}
      </StyledTypography>
    </DisplayInfo>
  );

  const showErrorMessage = () => (
    <DisplayInfo>
      <StyledTypography variant="h6" color="red" align="center" topGutter={4} bottomGutter={4}>
        {REVIEW_TEXTS.GOALS.ERROR_MSG}
      </StyledTypography>
    </DisplayInfo>
  );

  useEffect(() => {
    setGoals(null);
    fetchData();
  }, [reviewId, employeeId, providerId, responseId, refetchForm]);

  const updatedFirstColumnName = keyResultsName;

  console.log({ colors });

  return (
    <Container>
      {loading && showLoader()}
      {!loading && goals === null && showErrorMessage()}
      {!loading &&
        goals !== null &&
        (goals.length === 0 ? (
          showNoGoalsMessage()
        ) : (
          <Grid container>
            <Grid item xs={isManagerView ? 8 : 12} style={{ borderRight: '1px solid #E1E1E1' }}>
              {isPreview && (
                <Grid item style={{ border: '1px solid #E1E1E1', borderBottom: 'none', padding: '16px' }}>
                  <Flex alignItems="center" gap="12px">
                    <StyledTypography wordBreak variant="body1" color="textPrimary">
                      View Template For:
                    </StyledTypography>
                    <RadioGroup onChange={setPreviewTemplateView} value={previewTemplateView}>
                      <Flex gap="12px">
                        <Radio value="SELF" isChecked={previewTemplateView === 'SELF'}>
                          Self Review
                        </Radio>
                        <Radio value="MANAGER" isChecked={previewTemplateView === 'MANAGER'}>
                          Manager Review
                        </Radio>
                        <Radio value="PEER" isChecked={previewTemplateView === 'PEER'}>
                          Peer Review
                        </Radio>
                        <Radio value="UPWARD" isChecked={previewTemplateView === 'UPWARD'}>
                          Upward Review
                        </Radio>
                      </Flex>
                    </RadioGroup>
                  </Flex>
                </Grid>
              )}
              <StyledTable aria-label="Goal Table">
                <TableHeader
                  headerList={columnsList(isManagerView, updatedFirstColumnName)[columnsView]}
                  headerBoxJustify="left"
                />
                <TableBody>
                  {goals.map(({ goal, questions }) => (
                    <GoalQuestions
                      goal={goal}
                      questions={questions}
                      submitAnswer={answerQuestion}
                      columnsView={columnsView}
                      isSubmitClicked={isSubmitClicked}
                      isManagerView={isManagerView}
                      isShowGoalWeightage={isShowGoalWeightage}
                      refreshData={fetchData}
                      isGoalSectionEditable={isGoalSectionEditable}
                      isPreview={isPreview}
                      isViewOnlyModeEnabled={questions.isViewOnlyModeEnabled}
                      previewTemplateView={previewTemplateView}
                    />
                  ))}
                </TableBody>
              </StyledTable>
            </Grid>
            {isManagerView && (
              <StickyGridContainer item xs={4}>
                <PeerFeedbackComp reviewId={reviewId} employeeId={employeeId} />
              </StickyGridContainer>
            )}
            {isShowGoalRating && (
              <RatingContainer container padding="1rem">
                <StyledGrid item sm={3}>
                  <StyledTypography color="textSecondary">Overall goal score</StyledTypography>
                </StyledGrid>
                <StyledGrid item sm={3}>
                  <StyledTypography>
                    {goalRating.averageGoalRating !== undefined ? goalRating.averageGoalRating.toFixed(2) : 'NA'}
                  </StyledTypography>
                </StyledGrid>
              </RatingContainer>
            )}
          </Grid>
        ))}
    </Container>
  );
};

export default GoalForm;
